import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import {
  fetchMarketplaceConnections,
  MARKETPLACE_CONNECTIONS_KEY,
} from "@/api/use-marketplace-connections";
import { PageHeader } from "@/shared/components/PageHeader";
import { toast } from "@/shared/lib/toast";
import { Button } from "@/shared/ui/button";

import type { ConnectResult } from "../api/use-connect";
import { useConnect } from "../api/use-connect";
import { useDisconnect } from "../api/use-disconnect";
import { ConnectionsTable } from "../components/ConnectionsTable";
import { ConnectReplaceDialog } from "../components/ConnectReplaceDialog";
import { DisconnectDialog } from "../components/DisconnectDialog";
import { groupByMarketplace } from "../lib/group";
import { MARKETPLACE_LABEL_KEY } from "../lib/labels";
import type { Connection, MarketplaceId } from "../lib/types";

/** ?status=success | ?status=error carried by the OAuth callback redirect. */
export interface OAuthResultSearch {
  status?: "success" | "error";
}

interface ConnectionsPageProps {
  /** OAuth callback result, extracted from the route's search params. */
  oauthResult?: OAuthResultSearch;
  /** Called once the OAuth result toast has been shown, so the route can
   *  strip `?status=...` from the URL. */
  onOAuthResultHandled?: () => void;
}

function ConnectionsSkeleton(): JSX.Element {
  return (
    <div className="space-y-2" data-testid="marketplace-connections-loading">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="h-14 animate-pulse rounded-[var(--r-md)] bg-[var(--surface-sunken)]"
        />
      ))}
    </div>
  );
}

export function ConnectionsPage({
  oauthResult,
  onOAuthResultHandled,
}: ConnectionsPageProps): JSX.Element {
  const { t } = useTranslation("marketplace_connections");
  const {
    data: connections,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: MARKETPLACE_CONNECTIONS_KEY,
    queryFn: fetchMarketplaceConnections,
    // This is a monitoring surface: a connection can die while it's open,
    // and a stale "Connected" badge is actively misleading. The other
    // surfaces sharing this query key (orders filters/export, promotion's
    // seller dropdown) don't need this and rely on the hook's plain staleTime.
    refetchInterval: 60_000,
  });
  const connect = useConnect();
  const disconnect = useDisconnect();
  const [pendingDisconnect, setPendingDisconnect] = useState<Connection | null>(null);
  const [pendingConnect, setPendingConnect] = useState<{
    marketplace: MarketplaceId;
    result: ConnectResult;
  } | null>(null);

  // Show the OAuth-redirect toast exactly once per landing, regardless of
  // how many times this component re-renders. Deferred a tick past this
  // effect so it always fires after the app shell's <Toaster/> has mounted
  // and subscribed, no matter where this page sits relative to it in the
  // provider tree.
  const handledStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!oauthResult?.status || handledStatus.current === oauthResult.status) return;
    handledStatus.current = oauthResult.status;
    const status = oauthResult.status;
    void Promise.resolve().then(() => {
      if (status === "success") {
        toast.success(t("toast_oauth_success"));
      } else {
        toast.error(t("toast_oauth_error"));
      }
      onOAuthResultHandled?.();
    });
  }, [oauthResult?.status, onOAuthResultHandled, t]);

  function handleConnect(marketplace: MarketplaceId): void {
    connect.mutate(marketplace, {
      onSuccess: (result) => {
        if (result.currentActiveShop) {
          setPendingConnect({ marketplace, result });
        } else {
          window.location.href = result.authUrl;
        }
      },
      onError: () => toast.error(t("toast_connect_failed")),
    });
  }

  function handleDisconnectConfirm(): void {
    if (!pendingDisconnect) return;
    disconnect.mutate(pendingDisconnect.marketplace, {
      onSuccess: () => {
        toast.success(t("toast_disconnect_success"));
        setPendingDisconnect(null);
      },
      onError: () => toast.error(t("toast_disconnect_failed")),
    });
  }

  return (
    <div data-testid="marketplace-connections-page">
      <PageHeader title={t("title")} subtitle={t("subtitle")} />

      {isError ? (
        <div
          className="flex flex-col items-center gap-3 rounded-[var(--r-md)] border border-[var(--border-subtle)] bg-[var(--surface-raised)] py-16 text-center text-sm text-[var(--text-secondary)] shadow-[var(--sh-1)]"
          data-testid="marketplace-connections-error"
        >
          <p>{t("error")}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            data-testid="marketplace-connections-retry"
          >
            {t("retry")}
          </Button>
        </div>
      ) : isLoading ? (
        <ConnectionsSkeleton />
      ) : (
        <ConnectionsTable
          rows={groupByMarketplace(connections ?? [])}
          onConnect={handleConnect}
          onDisconnect={setPendingDisconnect}
          isConnecting={connect.isPending}
          isDisconnecting={disconnect.isPending}
        />
      )}

      <DisconnectDialog
        connection={pendingDisconnect}
        marketplaceLabel={
          pendingDisconnect ? t(MARKETPLACE_LABEL_KEY[pendingDisconnect.marketplace]) : ""
        }
        onConfirm={handleDisconnectConfirm}
        onCancel={() => setPendingDisconnect(null)}
        isPending={disconnect.isPending}
      />

      <ConnectReplaceDialog
        currentActiveShop={pendingConnect?.result.currentActiveShop ?? null}
        marketplaceLabel={
          pendingConnect ? t(MARKETPLACE_LABEL_KEY[pendingConnect.marketplace]) : ""
        }
        onConfirm={() => {
          if (!pendingConnect) return;
          const authUrl = pendingConnect.result.authUrl;
          setPendingConnect(null);
          window.location.href = authUrl;
        }}
        onCancel={() => setPendingConnect(null)}
        isPending={connect.isPending}
      />
    </div>
  );
}
